import React, { FC, forwardRef } from 'react';
import MaterialTable, { Column, Action, MTableToolbar } from 'material-table';
import { Paper, Grid, makeStyles, IconButton, Avatar, TextField } from '@material-ui/core';
import TimePicker, { TimePickerValue } from 'react-time-picker';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAdd';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import AddIcon from '@material-ui/icons/Add';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { useLanguageContext } from '../languages';
import { CricketTeamConfig, Period, Team } from '../../utils/types';
import theme from '../../theme';
import SimpleTextInfo from '../common/SimpleTextInfo';
import DateCell from '../common/DateCell';
import { getLocalTimezone } from '../../utils/dateTransform';
import '../common/react-time-picker.css';
import { Alert } from '@material-ui/lab';
import Dropdown from '../common/Dropdown';
import Autocomplete from '@material-ui/lab/Autocomplete';
import csvDownload from 'json-to-csv-export';
import { flattenObject, formatEndScore } from './exportHelpers';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import { filterDriversByTeamId, filterTeamByDriver } from '../../utils/f1TeamsAndDrivers';

import type { IdLabelPair } from '../../utils/types';

const unknownValue = '';

const getPeriodLengthInSeconds = (period: Period): number => {
	if (period.source?.startTime && period.source?.endTime) {
		return dayjs(period.source.endTime).diff(dayjs(period.source.startTime), 'second') + 1;
	}

	return 0;
};

const getPeriodsLengthInSeconds = (periods: Period[]): string => {
	let seconds = 0;
	periods.forEach((period: Period) => {
		seconds += getPeriodLengthInSeconds(period);
	});

	return `(${seconds}s)`;
};

const renderSegmentIdSection = (rowData: Period, sectionPosition: number): string => {
	const { startGameTime } = rowData;
	if (startGameTime) {
		const { segmentId } = startGameTime;
		if (segmentId) {
			const segmentArray = segmentId.split('.');
			return segmentArray[sectionPosition] ?? unknownValue;
		} else {
			return unknownValue;
		}
	} else {
		return unknownValue;
	}
};

const isPeriodEditorial = (rowData: Period, editorialPeriodPlayIds: string[]): boolean => {
	if (!editorialPeriodPlayIds || editorialPeriodPlayIds.length === 0 || !rowData.playId) return false;
	return editorialPeriodPlayIds.includes(rowData.playId!);
};

const isPeriodOriginUnknow = (periodPlayId: string, autoAndEditorialPlayIds: string[]): boolean => {
	// if autoAndEditorialPlayIds is empty then no checks applied
	return autoAndEditorialPlayIds.length > 0 ? !autoAndEditorialPlayIds.includes(periodPlayId) : false;
};

const toPeriod = (editablePeriod: EditablePeriod): Period => {
	const { firstSegment, secondSegment, thirdSegment, fourthSegment, data } = editablePeriod;
	if (data.startGameTime) {
		data.startGameTime.segmentId = [firstSegment, secondSegment, thirdSegment, fourthSegment].filter((item) => item !== unknownValue).join('.');
		data.startGameTime.segment = parseInt(firstSegment);
	}

	if (data.type === 'Text') data.source.endTime = data.source.startTime;
	data.source.startTime = data.source.startTime && dayjs(data.source.startTime).toISOString();
	data.source.endTime = data.source.endTime && dayjs(data.source.endTime).toISOString();

	return data;
};

export const sortAlphabetically = (gotList: string[]): string[] => {
	return gotList.sort((item1, item2) => item1.localeCompare(item2));
};

export const getSportTitleDropdown = (dropDownOptions: Record<string, unknown>, sport: string, type: string, leagueId: string): string[] => {
	const sportLower = sport?.toLowerCase() || undefined;

	if (sportLower === 'cricket') {
		return sortAlphabetically(dropDownOptions.cricketEventList ? (dropDownOptions.cricketEventList as string[]) : []);
	} else if (sportLower === 'golf') {
		return sortAlphabetically(dropDownOptions.golfEventList ? (dropDownOptions.golfEventList as string[]) : []);
	} else if (sportLower === 'football') {
		return sortAlphabetically(dropDownOptions.footballEventList ? (dropDownOptions.footballEventList as string[]) : []);
	} else if (sportLower === 'f1' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.f1ActionEventList ? (dropDownOptions.f1ActionEventList as string[]) : []);
	} else if (sportLower === 'f1') {
		return sortAlphabetically(dropDownOptions.f1TextEventList ? (dropDownOptions.f1TextEventList as string[]) : []);
	} else if (sportLower === 'americanfootball') {
		return sortAlphabetically(dropDownOptions.americanfootballEventList ? (dropDownOptions.americanfootballEventList as string[]) : []);
	} else if (sportLower === 'basketball') {
		return sortAlphabetically(dropDownOptions.basketballEventList ? (dropDownOptions.basketballEventList as string[]) : []);
	} else if (sportLower === 'baseball') {
		return sortAlphabetically(dropDownOptions.baseballEventList ? (dropDownOptions.baseballEventList as string[]) : []);
	}

	return [];
};

export const getSportTeamDropdown = (
	dropDownOptions: Record<string, unknown>,
	sport: string,
	type: string,
	leagueId: string | undefined,
	teamId: string | undefined,
	teams?: string[]
): string[] => {
	const sportLower = sport?.toLowerCase() || undefined;

	if (sportLower === 'cricket' && type === 'Action' && teams) {
		return sortAlphabetically(teams);
	} else if (sportLower === 'f1' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.f1TeamsList ? (dropDownOptions.f1TeamsList as string[]) : []);
	}

	return [];
};

export const getSportShortTextDropdown = (
	dropDownOptions: Record<string, unknown>,
	sport: string,
	type: string,
	leagueId: string | undefined,
	teamId: string | undefined
): string[] => {
	const sportLower = sport?.toLowerCase() || undefined;

	if (sportLower === 'cricket' && type === 'Action') {
		let playerList: string[] = [];

		if (dropDownOptions.cricketTeamsList) {
			(dropDownOptions.cricketTeamsList as CricketTeamConfig[]).forEach((team) => {
				if (teamId) {
					const idList = team.id.split('||');
					if (idList.includes(teamId)) {
						playerList = [...playerList, ...team.players];
					}
				} else {
					playerList = [...playerList, ...team.players];
				}
			});
		}

		return sortAlphabetically(playerList);
	} else if (sportLower === 'f1' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.f1DriverNameList ? (dropDownOptions.f1DriverNameList as string[]) : []);
	} else if (sportLower === 'golf' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.golfPlayerNameList ? (dropDownOptions.golfPlayerNameList as string[]) : []);
	}

	return [];
};

const useStyles = makeStyles((theme) => ({
	spacing: {
		marginBottom: theme.spacing(2)
	},
	dropdowns: {
		minWidth: '125px'
	},
	timePicker: {
		display: 'inline-block',
		minWidth: '100px'
	},
	primaryButton: {
		backgroundColor: theme.palette.primary.main
	}
}));

const Periods: FC<PeriodsProps> = (props) => {
	const {
		hideDetails,
		teams,
		sport,
		leagueId,
		gameStartTime,
		currentPeriod,
		checkPlayIdOrigin,
		editorialPeriodPlayIds,
		draftPeriodPlayIds,
		gameTitle,
		highlightId,
		onRowAdd,
		onRowPick,
		onRowDelete,
		onAllDelete,
		onRowPlay,
		onRowUpdate,
		periods
	} = props;

	const classes = useStyles();
	const { tableLocalization } = useLanguageContext();

	// prettier-ignore
	const actions = [
        (rowData: EditablePeriod): Action<EditablePeriod> => ({
            icon: (): JSX.Element => (rowData.data.type === 'Text' ? <TextFieldsIcon /> : <PlayArrowIcon />),
            tooltip: rowData.data.type === 'Text' ? 'Text Event' : 'Play',
            isFreeAction: false,
            disabled: rowData.data.type === 'Text' || !onRowPlay,
            onClick: (
                event: unknown,
                _data: EditablePeriod | EditablePeriod[]
            ): void => {
                const data =
                    _data instanceof Array
                        ? _data.map((item) => item.data)[0]
                        : _data.data;
                onRowPlay && onRowPlay(event, data);
            },
        }),
        ...(onRowPick
            ? [
                {
                    icon: (): JSX.Element => <AddIcon data-testid="select-period-icon" />,
                    tooltip: 'Select',
                    onClick: (
                        event: unknown,
                        _data: EditablePeriod | EditablePeriod[]
                    ): void => {
                        const data =
                            _data instanceof Array
                                ? _data.map((item) => item.data)
                                : [_data.data];
                        onRowPick(event, data);
                    },
                },
                {
                    icon: (): JSX.Element => <PlaylistAddCheckIcon />,
                    isFreeAction: true,
                    tooltip: 'Select all',
                    onClick: (
                        event: unknown,
                        _data: EditablePeriod | EditablePeriod[]
                    ): void => {
                        onRowPick(event, periods);
                    },
                },
            ]
            : []),
        ...(onAllDelete
            ? [
                {
                    icon: (): JSX.Element => <DeleteSweepIcon />,
                    isFreeAction: true,
                    tooltip: 'Remove all',
                    onClick: (
                        event: unknown,
                        _data: EditablePeriod | EditablePeriod[]
                    ): void => {
                        onAllDelete();
                    },
                },
            ]
            : [])
    ];

	const playIds = periods
		.filter((period): boolean => period.type !== 'Text')
		.map((period) => period.playId)
		.filter((playId) => playId) as string[];

	const unknowOriginPlayIds: string[] =
		draftPeriodPlayIds && draftPeriodPlayIds.length > 0 && checkPlayIdOrigin
			? playIds.filter((playId) => isPeriodOriginUnknow(playId, draftPeriodPlayIds))
			: [];

	const getIdByTeamName: { [key: string]: string } = {};
	teams &&
		teams.forEach((team) => {
			if (team.name) getIdByTeamName[team.name] = team.id!;
		});

	const updateTeamIdAndName = (rowData: EditablePeriod, newTeamNameValue: string | null): EditablePeriod => {
		if (newTeamNameValue && rowData.data.team) {
			rowData.data.team.name = newTeamNameValue;
			rowData.data.team.id = getIdByTeamName[newTeamNameValue];
		}
		return rowData;
	};

	const getSportColumns = (): Column<EditablePeriod>[] => {
		const sportLower = sport?.toLowerCase() ?? '';

		if (sportLower === 'f1') {
			return [
				{
					title: 'Lap',
					field: 'firstSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Team',
					field: 'data.team.id',
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					},
					editComponent: (editProps): JSX.Element => {
						const selectedDriver = editProps?.rowData?.data?.shortText ?? '';

						// Array of objects like: { id: "hash-1", label: "Ferrari" }
						const teamOptions = filterTeamByDriver(props.teams, selectedDriver);

						return (
							<Autocomplete<IdLabelPair>
								autoSelect={true}
								onChange={(_, newValue: IdLabelPair | null): void => editProps.onChange(newValue?.id ?? null)}
								options={teamOptions}
								// Tells the component how to compare the "value" to the "options"
								// and keeps the dropdown from highlighting the wrong item (The Logic)
								// Tells MUI that the object in your value matches the object in your options list by comparing IDs.
								getOptionSelected={(option, value): boolean => option.id === value.id}
								// Converts the object {id: "hash-1", label: "Alpine"} into the string "Alpine" for the UI.
								// Prevents the [object Object] error (The Display)
								getOptionLabel={(option): string => option?.label ?? ''}
								// Match the ID in the table to the object in the list
								value={teamOptions.find((opt): boolean => opt.id === editProps.value) || null}
								renderInput={(params): JSX.Element => <TextField {...params} className={classes.dropdowns} />}
							/>
						);
					},
					render: (rowData: EditablePeriod): JSX.Element => {
						// 1. Find the current team object using the ID stored in the row
						const currentTeamId = rowData.data.team?.id;

						/**
						 * The Periods component is used in both the Composer and the HighlightDetails components.
						 *
						 * The Composer passes the "teams" prop, while the HighlightDetails passes the "periods" props.
						 * Both the props contain team data but they have a different data structure.
						 *
						 * This condition is allowing the component to source the team data in both scenarios.
						 */

						let team: Team | undefined;

						if (props?.teams) {
							// console.log(`props.teams: ${JSON.stringify(props.teams)}`);
							team = props.teams?.find((t): boolean => t.id === currentTeamId);
						} else if (props?.periods) {
							// console.log(`props.periods: ${JSON.stringify(props.periods)}`);
							const period = props.periods?.find((p: Period): boolean => p?.team?.id === currentTeamId);
							team = period?.team || undefined;
						}

						// 2. Use teamInfo to render the logo/name
						const logoUrl = team?.logoUrl;
						const teamName = team?.name || currentTeamId || '';

						/*
						 * Show the team logo if the logoUrl property exists.
						 * If the URL is invalid, hide the image and show the team name instead.
						 * If there is no logoUrl property, show the team name (which is the team ID property).
						 * If there is no team found, show an empty string.
						 *
						 * New data has a logoUrl property, but old data do not, hence the fallback for back compatibility.
						 * The onError is to handle the case where the logoUrl is invalid (e.g. 404 error)
						 * This is to prevent broken image icons from showing in the UI.
						 * The team name is shown in a span with display: none by default, and only shown if the image fails to load.
						 * If there is no team found (which should not happen), we return an empty string to avoid rendering issues.
						 */
						return logoUrl ? (
							<>
								<img
									src={logoUrl || ''}
									alt={teamName || ''}
									title={teamName || ''}
									width='40px'
									onError={(e): void => {
										e.currentTarget.style.display = 'none';
										if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = 'block';
									}}
								/>
								<span className='fallback-text' style={{ display: 'none' }}>
									{teamName || ''}
								</span>
							</>
						) : (
							<span>{teamName}</span>
						);
					}
				},
				{
					title: 'Driver name',
					field: 'data.shortText',
					editComponent: (editProps): JSX.Element => {
						const selectedTeamId = editProps?.rowData?.data?.team?.id ?? '';

						return (
							<Autocomplete
								autoSelect={true}
								disableClearable={false}
								selectOnFocus={false}
								onChange={(event: unknown, newValue: string | null): void => editProps.onChange(newValue)}
								options={filterDriversByTeamId(props.teams, selectedTeamId)}
								value={editProps.value ?? null}
								renderInput={(params): JSX.Element => <TextField {...params} className={classes.dropdowns} />}
							/>
						);
					}
				}
			];
		}

		let columns: Column<EditablePeriod>[] = [];

		if (sportLower === 'golf') {
			columns = [
				{
					title: 'Round',
					field: 'firstSegment',
					type: 'string',
					filtering: false
				},
				{
					title: 'Hole',
					field: 'secondSegment',
					type: 'string',
					filtering: false
				},
				{
					title: 'Yardage',
					field: 'thirdSegment',
					type: 'string',
					filtering: false
				}
			];
		} else if (sportLower === 'football' || sportLower === 'americanfootball') {
			columns = [
				{
					title: 'Team',
					field: 'data.logoUrl',
					editable: 'never',
					filtering: false,
					render: (row: EditablePeriod): JSX.Element => <img src={row.data.team?.logoUrl || ''} alt={row.data.team?.name || ''} width='40px' />
				},
				{
					title: 'Segment',
					field: 'firstSegment',
					editable: 'never',
					filtering: false
				}
			];
		} else if (sportLower === 'cricket') {
			columns = [
				{
					title: 'Innings',
					field: 'firstSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Balls',
					field: 'secondSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Wickets',
					field: 'thirdSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Overs',
					field: 'fourthSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Team',
					field: 'data.team.name',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					},
					editComponent: (props): JSX.Element => (
						<Dropdown
							autoSelect={true}
							onChange={(event: unknown, newValue: string | null): void => props.onRowDataChange(updateTeamIdAndName(props.rowData, newValue))}
							getDropdownFunction={getSportTeamDropdown}
							type={props.rowData.data?.type ?? ''}
							teams={Object.keys(getIdByTeamName)}
							sport={sport}
							editComponentProps={props}
							classes={classes}
						/>
					)
				}
			];
		} else if (sportLower === 'baseball') {
			columns = [
				{
					title: 'Team',
					field: 'data.logoUrl',
					editable: 'never',
					filtering: false,
					render: (row: EditablePeriod): JSX.Element => <img src={row.data.team?.logoUrl || ''} alt={row.data.team?.name || ''} width='40px' />
				},
				{
					title: 'Inning',
					field: 'firstSegment',
					editable: 'never',
					filtering: false,
					lookup: {
						'1t': 'Top of the 1st',
						'1b': 'Bottom of the 1st',
						'2t': 'Top of the 2nd',
						'2b': 'Bottom of the 2nd',
						'3t': 'Top of the 3rd',
						'3b': 'Bottom of the 3rd',
						'4t': 'Top of the 4th',
						'4b': 'Bottom of the 4th',
						'5t': 'Top of the 5th',
						'5b': 'Bottom of the 5th',
						'6t': 'Top of the 6th',
						'6b': 'Bottom of the 6th',
						'7t': 'Top of the 7th',
						'7b': 'Bottom of the 7th',
						'8t': 'Top of the 8th',
						'8b': 'Bottom of the 8th',
						'9t': 'Top of the 9th',
						'9b': 'Bottom of the 9th',
						'10t': 'Top of the 10th',
						'10b': 'Bottom of the 10th',
						'11t': 'Top of the 11th',
						'11b': 'Bottom of the 11th',
						'12t': 'Top of the 12th',
						'12b': 'Bottom of the 12th',
						'13t': 'Top of the 13th',
						'13b': 'Bottom of the 13th',
						'14t': 'Top of the 14th',
						'14b': 'Bottom of the 14th',
						'15t': 'Top of the 15th',
						'15b': 'Bottom of the 15th',
						'16t': 'Top of the 16th',
						'16b': 'Bottom of the 16th',
						'17t': 'Top of the 17th',
						'17b': 'Bottom of the 17th',
						'18t': 'Top of the 18th',
						'18b': 'Bottom of the 18th',
						'19t': 'Top of the 19th',
						'19b': 'Bottom of the 19th',
						'20t': 'Top of the 20th',
						'20b': 'Bottom of the 20th'
					}
				},
				{
					title: 'Outs',
					field: 'data.sportData.outs',
					editable: 'never',
					filtering: false
				}
			];
		}

		if (['golf', 'football', 'americanfootball', 'cricket', 'baseball'].includes(sportLower)) {
			columns.push({
				title: 'Player name',
				field: 'data.shortText',
				filtering: false,
				editable: (_, rowData: EditablePeriod): boolean => {
					const isNewPeriod = rowData === undefined;
					const isTextPeriod = rowData?.data?.type === 'Text';
					return !isNewPeriod && !isTextPeriod;
				},
				editComponent: (props): JSX.Element => (
					<Dropdown
						autoSelect={true}
						freeSolo={true}
						disableClearable={sport?.toLowerCase() !== 'cricket'}
						selectOnFocus={false}
						onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
						getDropdownFunction={getSportShortTextDropdown}
						type={props.rowData.data?.type ?? ''}
						teamId={props.rowData.data?.team?.id}
						required={true}
						sport={sport}
						leagueId={leagueId ?? undefined}
						editComponentProps={props}
						classes={classes}
					/>
				)
			});
		}

		return columns;
	};

	const isEditorialClipColumn = {
		title: 'Editorial',
		field: 'editorial',
		type: 'boolean',
		filtering: true
	};

	// prettier-ignore
	const columns = [
        {
            title: 'Title',
            field: 'data.title',
            filtering: false,
            editComponent: (props): JSX.Element => (
                <Dropdown
                    autoSelect={true}
                    freeSolo={(sport?.toLocaleLowerCase() === 'golf') ? false : true}
                    disableClearable={true}
                    onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
                    getDropdownFunction={getSportTitleDropdown}
                    type={props.rowData.data?.type ?? ''}
                    required={true}
                    sport={sport}
                    leagueId={leagueId ?? ''}
                    editComponentProps={props}
                    classes={classes}
                />
            ),
        },
        {
            title: `Time (${getLocalTimezone()})`,
            field: 'data.source.startTime',
            filtering: false,
            editable: (_, rowData: EditablePeriod): boolean => {
                const isNewPeriod = rowData === undefined;
                const isTextPeriod = rowData?.data?.type === 'Text';
                return isNewPeriod || isTextPeriod;
            },
            editComponent: (props): JSX.Element => {
                if (!props.value) props.onChange(dayjs(gameStartTime).toDate()); // workaround

                return (
                    <TimePicker
                        className={classes.timePicker}
                        name="startTime"
                        value={dayjs(props.value || gameStartTime).toDate()}
                        format="HH:mm:ss"
                        maxDetail="second"
                        onChange={(value: TimePickerValue): void => {
                            if (typeof value === 'string') {
                                const timeParts = value.split(':');
                                const newValue = dayjs(gameStartTime)
                                    .set('hour', Number(timeParts[0]))
                                    .set('minute', Number(timeParts[1]))
                                    .set('second', Number(timeParts[2]))
                                    .toDate();
                                props.onChange(newValue);
                            }
                        }}
                        clearIcon={null}
                        disableClock={true}
                    />
                );
            },
            render: (row: EditablePeriod): JSX.Element => (
                <DateCell
                    value={row.data.source?.startTime ?? ''}
                    showTimezone={false}
                    showTimeOnly={true}
                />
            ),
        },
        {
            title:
                sport?.toLowerCase() === 'f1'
                    ? 'Position'
                    : sport?.toLowerCase() === 'cricket'
                        ? 'Runs'
                        : 'Score',
            field: 'data.endScore',
            filtering: false,
            editable: (_, rowData: EditablePeriod): boolean => {
                const isNewPeriod = rowData === undefined;
                const isTextPeriod = rowData?.data?.type === 'Text';
                return !isNewPeriod && !isTextPeriod;
            },
						hidden: sport ? ['baseball'].includes(sport.toLowerCase()) : false
        },
        ...(sport ? getSportColumns() : []),
        ...((unknowOriginPlayIds.length)
            ? [{
                title: 'Warn',
                field: 'warn',
                type: 'boolean',
            }]
            : [...(editorialPeriodPlayIds && editorialPeriodPlayIds.length && !checkPlayIdOrigin
                ? [isEditorialClipColumn]
                : [])]),
    ] as Column<EditablePeriod>[];

	const detailsPanel = (rowData: EditablePeriod): JSX.Element => {
		const period = rowData.data;

		return (
			<Grid container direction='column' spacing={2} style={{ margin: theme.spacing(2) }}>
				<SimpleTextInfo label='Period ID' value={period.id || 'Not specified'} compact />
				<SimpleTextInfo label='Play ID' value={period.playId || 'Not specified'} compact />
				<SimpleTextInfo label='Description' value={period.description || 'Not specified'} compact />
				<SimpleTextInfo label='Type' value={period.type === 'Text' ? 'Text Event' : 'Video Event'} compact />
			</Grid>
		);
	};

	const handlePeriodAdd = async (newData: EditablePeriod): Promise<unknown> => {
		if (onRowAdd) {
			const { title, source } = newData.data;
			const startTime = source.startTime && dayjs(source.startTime).toISOString();

			return await onRowAdd({
				title,
				source: {
					...source,
					startTime,
					endTime: startTime,
					type: 'Text'
				},
				id: uuidv4(),
				playId: uuidv4(),
				type: 'Text'
			});
		}
	};

	const handlePeriodUpdate = async (newData: EditablePeriod, oldData?: EditablePeriod): Promise<unknown> => {
		if (onRowUpdate) {
			const newPeriod = toPeriod(newData);
			return await onRowUpdate(newPeriod, oldData?.data);
		}
	};

	const handlePeriodDelete = async (oldPeriod: EditablePeriod): Promise<unknown> => {
		if (onRowDelete) {
			return await onRowDelete(oldPeriod.data);
		}
	};

	const rowStyle = (rowData: EditablePeriod): CSSProperties => {
		let styles = {};

		if (rowData.data === currentPeriod) {
			styles = {
				...styles,
				backgroundColor: theme.palette.primary.main,
				color: 'white'
			};
		}

		if (rowData.editorial) {
			styles = { ...styles, fontWeight: 'bold' };
		}

		if (rowData.warn) {
			styles = { ...styles, backgroundColor: 'yellow', color: theme.palette.error.main };
		}

		return styles;
	};

	const videoPeriods = periods.filter((period) => period.type !== 'Text');

	const exportHighlight = (): void => {
		const flattenedData = periods?.map((item): Record<string, unknown> | undefined => {
			if (item.endScore) item.endScore = formatEndScore(item.endScore);
			if (sport && !['basketball', 'baseball'].includes(sport.toLowerCase())) {
				delete item.excitementScore;
				delete item.excitementThreshold;
			}
			return flattenObject(item);
		});
		if (flattenedData) csvDownload({ data: flattenedData, delimiter: ',', filename: `${gameTitle}-${highlightId}` });
	};

	return (
		<div className={classes.spacing}>
			<MaterialTable
				localization={tableLocalization}
				actions={actions}
				columns={columns}
				detailPanel={hideDetails ? undefined : detailsPanel}
				editable={{
					onRowAdd: onRowAdd ? handlePeriodAdd : undefined,
					onRowDelete: onRowDelete ? handlePeriodDelete : undefined,
					onRowUpdate: onRowUpdate ? handlePeriodUpdate : undefined
				}}
				icons={{
					Export: forwardRef(() => (
						<IconButton size='small'>
							<Avatar className={classes.primaryButton}>
								<SaveAltIcon />
							</Avatar>
						</IconButton>
					))
				}}
				data={periods.map((period) => {
					const common = {
						data: period,
						firstSegment: renderSegmentIdSection(period, 0),
						secondSegment: renderSegmentIdSection(period, 1),
						thirdSegment: renderSegmentIdSection(period, 2),
						fourthSegment: renderSegmentIdSection(period, 3)
					};

					return unknowOriginPlayIds.length && period.playId
						? {
								...common,
								warn: unknowOriginPlayIds.includes(period.playId)
							}
						: {
								...common,
								editorial: isPeriodEditorial(period, editorialPeriodPlayIds || [])
							};
				})}
				options={{
					pageSize: 5,
					search: true,
					sorting: false,
					rowStyle,
					filtering: editorialPeriodPlayIds && editorialPeriodPlayIds.length && !checkPlayIdOrigin ? true : false,
					searchFieldStyle: {
						width: '150px'
					},
					exportAllData: true,
					exportButton: {
						csv: true,
						pdf: false
					},
					exportCsv: exportHighlight,
					emptyRowsWhenPaging: false
				}}
				title={`Events: ${periods.length} ${getPeriodsLengthInSeconds(videoPeriods)}`}
				components={
					unknowOriginPlayIds.length
						? {
								Container: (props): JSX.Element => <Paper {...props} data-testid='periods-table' />,
								Toolbar: (props): JSX.Element => (
									<>
										<MTableToolbar {...props} />
										<Alert severity='warning'>
											Items (<strong>Red Text and Highlighted</strong>): Could not be found in any automated and editorial highlights.
											<br />
											These items will not be copied over and appear in other territory's airing (e.g. in the case of F1).
											<br />
											If the item was an editorial clip which was removed then recreated: Please remove such clip items, and under{' '}
											<strong>Select a Source Highlight</strong> select the latest <strong>DRAFT</strong> highlight to add the most up-to-date clip.
										</Alert>
									</>
								)
							}
						: {
								Container: (props): JSX.Element => <Paper {...props} data-testid='periods-table' />
							}
				}
			/>
		</div>
	);
};

type EditablePeriod = {
	data: Period;
	firstSegment: string;
	secondSegment: string;
	thirdSegment: string;
	fourthSegment: string;
	editorial?: boolean;
	warn?: boolean;
};

type PeriodsProps = {
	teams?: Team[] | undefined;
	sport?: string | null;
	leagueId?: string | null;
	gameStartTime?: string | undefined;
	currentPeriod?: Period;
	periods: Period[];
	hideDetails?: boolean | null;
	checkPlayIdOrigin?: boolean | null;
	editorialPeriodPlayIds?: string[] | null;
	draftPeriodPlayIds?: string[] | null;
	gameTitle?: string | null;
	highlightId?: string | null;
	onRowAdd?: (newData: Period) => Promise<unknown>;
	onRowPick?: (event: unknown, data: Period | Period[]) => void;
	onRowDelete?: (oldData: Period) => Promise<unknown>;
	onAllDelete?: () => Promise<unknown>;
	onRowPlay?: (event: unknown, data: Period) => void;
	onRowUpdate?: (newData: Period, oldData?: Period) => Promise<unknown>;
};

export default Periods;
